import React, { useCallback, useMemo, type Ref } from 'react';
import memoizeOne from 'memoize-one';
import ShowMoreHorizontalIcon from '@atlaskit/icon/core/show-more-horizontal';
import Button from '@atlaskit/button';
import { IconButton } from '@atlaskit/button/new';
import DropdownMenu, {
	type CustomTriggerProps,
	DropdownItemGroup,
	type OnOpenChangeArgs,
	DropdownItem,
} from '@atlaskit/dropdown-menu';
import MoreVerticalIcon from '@atlaskit/icon/core/migration/show-more-vertical--more-vertical';
import ChevronRightLargeIcon from '@atlaskit/icon/core/migration/chevron-right--chevron-right-large';
import { Box, xcss } from '@atlaskit/primitives';
import Tooltip from '@atlaskit/tooltip';
import type { MessageDescriptorV2 as MessageDescriptor } from '@atlassian/jira-intl/src/v2/types.tsx';
import ShortcutScope from '@atlassian/jira-common-components-keyboard-shortcuts/src/shortcut-scope.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { mergeRefs } from '@atlassian/jira-merge-refs/src/index.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { isChrome } from '@atlassian/jira-common-util-browser/src/index.tsx';
import {
	MOVE_COLUMN_FIRST,
	MOVE_COLUMN_LAST,
	MOVE_COLUMN_LEFT,
	MOVE_COLUMN_RIGHT,
	SORT_ASC,
	SORT_DESC,
	SORT_NONE,
} from '../../../../../../common/constants.tsx';
import type { Column, TargetColumnPosition } from '../../../../../../common/types.tsx';
import { useIsInlineFieldConfigEnabled } from '../../../../../../controllers/features/selectors.tsx';
import { useIFCConfigContext } from '../../../../../../common/ui/inline-field-config/context/index.tsx';
import { useIFCEditField } from '../../../../../../common/ui/inline-field-config/edit-field/index.tsx';
import { ActionItem } from './action-item/index.tsx';
import messages from './messages.tsx';

export type Props = {
	column: Column;
	currentColumnWidth: number;
	hasColumnConfiguration: boolean;
	isColumnResizingEnabled: boolean;
	isMergedColumn?: boolean;
	isFirst: boolean;
	isLast: boolean;
	isOpen: boolean;
	triggerRef?: Ref<HTMLButtonElement>;
	onMoveColumn: (position: TargetColumnPosition) => void;
	onOpenChange: (isOpen: boolean) => void;
	onRemoveColumn: () => void;
	onResizeColumn: () => void;
	onResetColumnWidth: () => void;
	onFocus: () => void;
	onBlur: () => void;
};

const NestedSortingDropdown = ({
	column,
	customMessage,
}: {
	column: Column;
	customMessage: MessageDescriptor;
}) => {
	const { formatMessage } = useIntl();

	const sortAsc = useCallback(() => {
		column.onSortOrderChanged?.(column.jqlTerm, SORT_ASC);
	}, [column]);

	const sortDesc = useCallback(() => {
		column.onSortOrderChanged?.(column.jqlTerm, SORT_DESC);
	}, [column]);

	let title = column.title;
	if (column.currentSorting === SORT_ASC && column.ascText) {
		title = `${column.title}: ${formatMessage(messages.sortDirection, {
			sortDescription: column.ascText,
		})}`;
	} else if (column.currentSorting === SORT_DESC && column.descText) {
		title = `${column.title}: ${formatMessage(messages.sortDirection, {
			sortDescription: column.descText,
		})}`;
	}

	return (
		<DropdownMenu
			placement="right-start"
			shouldRenderToParent
			trigger={({ triggerRef, ...triggerProps }) => (
				<DropdownItem
					{...triggerProps}
					ref={triggerRef}
					elemAfter={
						<ChevronRightLargeIcon LEGACY_size="small" size="small" spacing="spacious" label="" />
					}
				>
					{title}
				</DropdownItem>
			)}
		>
			<DropdownItemGroup>
				<ActionItem
					actionType="sortAsc"
					onClick={sortAsc}
					isSelected={column.currentSorting === SORT_ASC}
				>
					{column.ascText ?? false
						? formatMessage(customMessage, {
								sortDescription: column.ascText,
							})
						: formatMessage(messages.sortAsc)}
				</ActionItem>
				<ActionItem
					actionType="sortDesc"
					onClick={sortDesc}
					isSelected={column.currentSorting === SORT_DESC}
				>
					{column.descText ?? false
						? formatMessage(customMessage, {
								sortDescription: column.descText,
							})
						: formatMessage(messages.sortDesc)}
				</ActionItem>
			</DropdownItemGroup>
		</DropdownMenu>
	);
};

export const MoreActions = ({
	column,
	currentColumnWidth,
	hasColumnConfiguration,
	isColumnResizingEnabled,
	isMergedColumn,
	isFirst,
	isLast,
	isOpen,
	triggerRef = null,
	onMoveColumn,
	onOpenChange,
	onRemoveColumn,
	onResizeColumn,
	onResetColumnWidth,
	onFocus,
	onBlur,
}: Props) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const { formatMessage } = useIntl();

	const isInlineFieldConfigEnabled = useIsInlineFieldConfigEnabled();

	const { ifcConfig } = fg('jira_inline_field_config_gate')
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useIFCConfigContext()
		: { ifcConfig: null };

	const editField = fg('jira_inline_field_config_gate')
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useIFCEditField()
		: undefined;

	const sortAsc = useCallback(() => {
		column.onSortOrderChanged?.(column.jqlTerm, SORT_ASC);
	}, [column]);

	const sortDesc = useCallback(() => {
		column.onSortOrderChanged?.(column.jqlTerm, SORT_DESC);
	}, [column]);

	const clearSorting = useCallback(() => {
		if (isMergedColumn) {
			column.mergedColumns?.forEach((mergedColumn) => {
				if (mergedColumn.currentSorting && mergedColumn.currentSorting !== SORT_NONE) {
					mergedColumn.onSortOrderChanged?.(mergedColumn.jqlTerm, SORT_NONE);
				}
			});
		} else {
			column.onSortOrderChanged?.(column.jqlTerm, SORT_NONE);
		}
	}, [column, isMergedColumn]);

	const handleOnOpenChange = useCallback(
		({ isOpen: newIsOpen }: OnOpenChangeArgs) => {
			if (newIsOpen) {
				fireUIAnalytics(
					createAnalyticsEvent({
						action: 'opened',
						actionSubject: 'dropdown',
					}),
					'issueTableHeaderActions',
				);
			}

			onOpenChange(newIsOpen);
		},
		[createAnalyticsEvent, onOpenChange],
	);

	const mergedColumns = column.mergedColumns ?? [];
	const shouldShowNestedSorting = Boolean(isMergedColumn && mergedColumns.length > 1);

	const isColumnSortable = useCallback(
		(col: Column) => Boolean(!!col.onSortOrderChanged && col.isSortable),
		[],
	);

	let isSortable = isColumnSortable(column);
	if (fg('jira_list_merged_column_gate')) {
		isSortable =
			isColumnSortable(column) ||
			Boolean(
				isMergedColumn && !shouldShowNestedSorting && column.mergedColumns?.some(isColumnSortable),
			);
	}

	const triggerLabel = formatMessage(messages.columnActions, { column: column.title });

	// Ensure a stable ref function is passed through to the trigger button
	const memoizedMergeRefs = useMemo(() => memoizeOne(mergeRefs), []);

	const customMessage = fg('jira_inline_field_config_gate')
		? messages.sortCustomNew
		: messages.sortCustom;

	return (
		<DropdownMenu
			isOpen={isOpen}
			onOpenChange={handleOnOpenChange}
			placement="bottom-start"
			trigger={({
				triggerRef: dropdownTriggerRef,
				...triggerProps
			}: CustomTriggerProps<HTMLButtonElement>) =>
				isVisualRefreshEnabled() ? (
					<IconButton
						{...triggerProps}
						ref={memoizedMergeRefs(dropdownTriggerRef, triggerRef)}
						icon={(iconProps) => <ShowMoreHorizontalIcon {...iconProps} size="small" />}
						label={triggerLabel}
						onFocus={onFocus}
						onBlur={onBlur}
						isTooltipDisabled={false}
						spacing="compact"
						appearance="subtle"
						testId="native-issue-table.ui.issue-table.header.header-cell.header-cell-actions.more-actions.icon-button"
					/>
				) : (
					<Tooltip content={triggerLabel} delay={0} ignoreTooltipPointerEvents>
						<Box xcss={triggerStyles}>
							<Button
								{...triggerProps}
								ref={memoizedMergeRefs(dropdownTriggerRef, triggerRef)}
								appearance="subtle"
								spacing="compact"
								iconBefore={<MoreVerticalIcon label={triggerLabel} LEGACY_size="small" />}
								onFocus={onFocus}
								onBlur={onBlur}
							/>
						</Box>
					</Tooltip>
				)
			}
			shouldRenderToParent={isChrome()}
		>
			{isSortable && !shouldShowNestedSorting && (
				<DropdownItemGroup>
					{column.currentSorting !== SORT_ASC && (
						<ActionItem actionType="sortAsc" onClick={sortAsc}>
							{column.ascText ?? false
								? formatMessage(customMessage, {
										sortDescription: column.ascText,
									})
								: formatMessage(messages.sortAsc)}
						</ActionItem>
					)}
					{column.currentSorting !== SORT_DESC && (
						<ActionItem actionType="sortDesc" onClick={sortDesc}>
							{column.descText ?? false
								? formatMessage(customMessage, {
										sortDescription: column.descText,
									})
								: formatMessage(messages.sortDesc)}
						</ActionItem>
					)}
					{column.currentSorting !== SORT_NONE && fg('jsc_list_reranking_gate') && (
						<ActionItem actionType="clearSorting" onClick={clearSorting}>
							{formatMessage(messages.clearSorting)}
						</ActionItem>
					)}
				</DropdownItemGroup>
			)}
			{shouldShowNestedSorting && (
				<DropdownItemGroup title={formatMessage(messages.sortByColumns)}>
					{mergedColumns.map((mergedColumn) => (
						<NestedSortingDropdown
							key={mergedColumn.id}
							column={mergedColumn}
							customMessage={customMessage}
						/>
					))}
					{mergedColumns.some((col) => col.currentSorting && col.currentSorting !== SORT_NONE) &&
						fg('jsc_list_reranking_gate') && (
							<ActionItem actionType="clearSorting" onClick={clearSorting}>
								{formatMessage(messages.clearSorting)}
							</ActionItem>
						)}
				</DropdownItemGroup>
			)}
			{hasColumnConfiguration && (
				<DropdownItemGroup hasSeparator={isSortable}>
					{!isFirst && (
						<>
							<ActionItem
								actionType="moveColumnFirst"
								onClick={() => onMoveColumn(MOVE_COLUMN_FIRST)}
							>
								{fg('jira_inline_field_config_gate')
									? formatMessage(messages.moveFirstNew)
									: formatMessage(messages.moveFirst)}
							</ActionItem>
							<ActionItem
								actionType="moveColumnLeft"
								onClick={() => onMoveColumn(MOVE_COLUMN_LEFT)}
							>
								{fg('jira_inline_field_config_gate')
									? formatMessage(messages.moveLeftNew)
									: formatMessage(messages.moveLeft)}
							</ActionItem>
						</>
					)}
					{!isLast && (
						<>
							<ActionItem
								actionType="moveColumnRight"
								onClick={() => onMoveColumn(MOVE_COLUMN_RIGHT)}
							>
								{fg('jira_inline_field_config_gate')
									? formatMessage(messages.moveRightNew)
									: formatMessage(messages.moveRight)}
							</ActionItem>
							<ActionItem
								actionType="moveColumnLast"
								onClick={() => onMoveColumn(MOVE_COLUMN_LAST)}
							>
								{fg('jira_inline_field_config_gate')
									? formatMessage(messages.moveLastNew)
									: formatMessage(messages.moveLast)}
							</ActionItem>
						</>
					)}
					<ActionItem actionType="removeColumn" onClick={onRemoveColumn}>
						{formatMessage(messages.removeColumn)}
					</ActionItem>
				</DropdownItemGroup>
			)}
			{isColumnResizingEnabled && (
				<DropdownItemGroup
					hasSeparator={isSortable || hasColumnConfiguration || shouldShowNestedSorting}
				>
					<ActionItem actionType="resizeColumn" onClick={onResizeColumn}>
						{formatMessage(messages.resizeColumn)}
					</ActionItem>
					{currentColumnWidth != null &&
						currentColumnWidth !== column.defaultWidth &&
						!isMergedColumn && (
							<ActionItem actionType="resetColumnWidth" onClick={onResetColumnWidth}>
								{formatMessage(messages.resetColumnWidth)}
							</ActionItem>
						)}
				</DropdownItemGroup>
			)}
			{isInlineFieldConfigEnabled && column.configureableFieldId && (
				<DropdownItemGroup hasSeparator>
					{ifcConfig?.IFCTriggerRef && editField && (
						<ifcConfig.IFCTriggerRef>
							{({ triggerRef: ifcTriggerRef }) => (
								<ActionItem
									ref={ifcTriggerRef}
									actionType="editField"
									onClick={() => {
										if (column.configureableFieldId) {
											editField(column.configureableFieldId, column.id);
										}
									}}
								>
									{formatMessage(messages.editField)}
								</ActionItem>
							)}
						</ifcConfig.IFCTriggerRef>
					)}
				</DropdownItemGroup>
			)}
			<ShortcutScope />
		</DropdownMenu>
	);
};

const triggerStyles = xcss({
	display: 'flex',
	height: '24px',
	width: '24px',
});
